import to from 'await-to-js';
import axios from 'axios';
import {
  flatMap,
  get,
  groupBy,
  includes,
  isEmpty,
  keyBy,
  map,
  merge,
  reduce,
  size,
  filter,
} from 'lodash';
import { defineStore } from 'pinia';
import { computed, reactive, toRefs } from 'vue';

// plugins and utils
import i18n from '@/plugins/vue-i18n';
import cannGroupUtils from '@/utils/cann-groups';

// enums
import jobapi from '@enums/jobapi';
import sectionStatuses from '@enums/section-status';
import SORT_DIRECTION from '@enums/sort-direction';
import WORK_PACKAGE_TYPE from '@enums/workpackage-type';

// composables
import baseStatus from '@sharedModules/data/baseStatus';
import { useDayjs } from '@/composables/use-dayjs';

// stores
// TODO: aov3-8283 fix circular dependency issue
// eslint-disable-next-line import/no-cycle
import { useContextStore } from '@/stores/context';
import { useEventsStore } from '@/stores/events';
import { useSnackbarStore } from '@/stores/snackbar';
// TODO: aov3-8283 fix circular dependency issue
// eslint-disable-next-line import/no-cycle
import { useWorkpackagesStore } from '@/stores/workpackages';
// TODO: aov3-8283 fix circular dependency issue
// eslint-disable-next-line import/no-cycle
import { useAssortmentCanvasStore } from '@/stores/assortment-canvas';

export const useScenariosStore = defineStore('scenarios', () => {
  // state
  const state = reactive({
    scenarios: [],
    selectedScenario: {},
    loading: false,
    saveInProgress: false,
    scenarioCannGroupsWithNotes: null,
  });

  /**
   * Previous Vuex mutations are still in use
   */

  function updateScenariosMutation(scenario) {
    state.scenarios = state.scenarios.map(el => (el._id === scenario._id ? scenario : el));
  }

  function setScenarioCannGroupsWithNotesMutation({ cannGroups }) {
    state.scenarioCannGroupsWithNotes = cannGroups;
  }

  function updateCannGroupNoteCountMutation({ cannGroupId, difference = 1 }) {
    const cannGroupToUpdate = state.scenarioCannGroupsWithNotes.find(cg => cg.key === cannGroupId);
    if (cannGroupToUpdate) {
      cannGroupToUpdate.totalNotes += difference;
    }
  }

  function updateNoteCountMutation({ scenarioId, difference = 1 }) {
    const scenarioToUpdate = state.scenarios.find(sc => sc._id === scenarioId);

    if (scenarioToUpdate) {
      scenarioToUpdate.totalNotes += difference;
    }

    if (state.selectedScenario._id === scenarioId) {
      state.selectedScenario.totalNotes += difference;
    }
  }

  /**
   * Getters
   */

  const getScenarioById = computed(() => id => {
    return state.scenarios.find(el => el._id === id);
  });

  const cannGroups = computed(() => {
    return cannGroupUtils.getLeafNodes(state.selectedScenario.cannGroups);
  });

  const stores = computed(() => {
    return state.selectedScenario.stores;
  });

  const storesInAssortmentCount = computed(() => {
    return size(filter(state.selectedScenario.stores, store => store.assortment));
  });

  const attributesById = computed(() => {
    return keyBy(state.selectedScenario.customAttributes, 'id');
  });

  const getCustomerFacingAndReportingAttributes = computed(() => {
    return state.selectedScenario.customAttributes;
  });

  const getUseMarkdownAndWasteToCalculateMarginAndPrice = computed(() => {
    const workpackageStore = useWorkpackagesStore();
    const useMarkdownAndWasteToCalculateMarginAndPrice = get(
      workpackageStore.selectedWorkpackage,
      'useMarkdownAndWasteToCalculateMarginAndPrice'
    );

    return useMarkdownAndWasteToCalculateMarginAndPrice;
  });

  const getSimpleSwapsClusteredStores = computed(() => {
    const workpackagesStore = useWorkpackagesStore();
    const localStores = map(stores.value, ({ simpleSwapsCluster, storeKey }) => ({
      simpleSwapsCluster,
      storeKey,
    }));
    const clusteredStores = workpackagesStore.isSimpleSwapsWP
      ? groupBy(localStores, 'simpleSwapsCluster')
      : [];
    return reduce(
      clusteredStores,
      (acc, cs, key) => {
        acc[key] = flatMap(cs, 'storeKey');
        return acc;
      },
      {}
    );
  });

  const scenarioStatus = computed(() => scenario => {
    const contextStore = useContextStore();
    const workpackageStore = useWorkpackagesStore();
    const wpType = get(workpackageStore.selectedWorkpackage, 'type');

    // Overwrite the actual status over the base based on WP type
    let actualStatus = { ...baseStatus.baseStatusObject };

    if (WORK_PACKAGE_TYPE.STANDARD.includes(wpType)) {
      actualStatus = merge(actualStatus, baseStatus.fullResetStatusObject, scenario.status);
    }

    if (wpType === WORK_PACKAGE_TYPE.ASSORTMENT_SIMPLE_SWAPS) {
      actualStatus = merge(actualStatus, baseStatus.simpleSwapsStatusObject, scenario.status);
    }

    // Calculate if general sections are available
    actualStatus.inputs.available = !isEmpty(state.selectedScenario);
    actualStatus.space.available = true;

    // Calculate if general sub sections are available for editing
    actualStatus.space.furniture.available = true;

    actualStatus.space.mapping.available =
      actualStatus.space.furniture.status === sectionStatuses.complete ||
      actualStatus.space.mapping.status !== sectionStatuses.notStarted;

    actualStatus.space.storeclass.available =
      actualStatus.space.mapping.status === sectionStatuses.complete ||
      actualStatus.space.storeclass.status !== sectionStatuses.notStarted;

    actualStatus.inputs.attributeEditor.available = true;

    actualStatus.inputs.cannGroups.available =
      actualStatus.inputs.attributeEditor.status === sectionStatuses.complete ||
      actualStatus.inputs.cannGroups.status !== sectionStatuses.notStarted;

    actualStatus.inputs.varietyGroups.available =
      actualStatus.inputs.cannGroups.status === sectionStatuses.complete ||
      actualStatus.inputs.varietyGroups.status !== sectionStatuses.notStarted;

    // Calculate availability for full reset specific sections
    if (WORK_PACKAGE_TYPE.STANDARD.includes(wpType)) {
      actualStatus.measuring.available =
        actualStatus.inputs.cannGroups.status === sectionStatuses.complete ||
        actualStatus.measuring.status !== sectionStatuses.notStarted;

      actualStatus.modelling.available =
        actualStatus.measuring.cdt.status === sectionStatuses.complete ||
        actualStatus.modelling.status !== sectionStatuses.notStarted;

      actualStatus.assortment.available =
        actualStatus.measuring.cdt.status === sectionStatuses.complete;
      actualStatus.execution.available = true;

      actualStatus.finalisation.available =
        actualStatus.assortment.status === sectionStatuses.complete;

      actualStatus.space.spacebreaks.available =
        actualStatus.space.storeclass.status === sectionStatuses.complete ||
        actualStatus.space.spacebreaks.status !== sectionStatuses.notStarted;

      actualStatus.measuring.switching.available =
        actualStatus.inputs.varietyGroups.status === sectionStatuses.complete ||
        actualStatus.measuring.switching.status !== sectionStatuses.notStarted;

      // cdt depends on switching, see: analytics/analytics/tasks/cdt_generation/cdt_generation.py:95
      actualStatus.measuring.cdt.available =
        (actualStatus.inputs.cannGroups.status === sectionStatuses.complete &&
          actualStatus.measuring.switching.status === sectionStatuses.complete) ||
        actualStatus.measuring.cdt.status !== sectionStatuses.notStarted;

      actualStatus.measuring.region.available =
        actualStatus.inputs.cannGroups.status === sectionStatuses.complete ||
        actualStatus.measuring.region.status !== sectionStatuses.notStarted;

      // The Clustering page availability should only depend on Region if the feature is enabled
      actualStatus.measuring.clustering.available =
        (get(contextStore.clientConfig, 'features.regionsByAttributeEnabled', false) &&
          actualStatus.measuring.region.status === sectionStatuses.complete) ||
        (!get(contextStore.clientConfig, 'features.regionsByAttributeEnabled', false) &&
          actualStatus.inputs.cannGroups.status === sectionStatuses.complete) ||
        actualStatus.measuring.clustering.status !== sectionStatuses.notStarted;

      actualStatus.modelling.productModelling.available =
        actualStatus.measuring.cdt.status === sectionStatuses.complete ||
        actualStatus.modelling.productModelling.status !== sectionStatuses.notStarted;

      // The Switching Modelling page should be enabled if either switching
      // or clustered switching was run (the user may use unclustered only)
      actualStatus.modelling.switchingModelling.available =
        actualStatus.measuring.switching.status === sectionStatuses.complete ||
        actualStatus.measuring.clustering.status === sectionStatuses.complete ||
        actualStatus.modelling.switchingModelling.status !== sectionStatuses.notStarted;
    }

    // Calculate availability for simple swaps specific sections
    if (wpType === WORK_PACKAGE_TYPE.ASSORTMENT_SIMPLE_SWAPS) {
      actualStatus.space.planograms.available =
        actualStatus.space.storeclass.status === sectionStatuses.complete ||
        actualStatus.space.planograms.status !== sectionStatuses.notStarted;

      actualStatus.inputs.prepareSwaps.available =
        actualStatus.inputs.varietyGroups.status === sectionStatuses.complete ||
        actualStatus.inputs.prepareSwaps.status !== sectionStatuses.notStarted;

      actualStatus.swaps.productsToSwap.available =
        actualStatus.space.storeclass.status === sectionStatuses.complete ||
        actualStatus.swaps.productsToSwap.status !== sectionStatuses.notStarted;

      actualStatus.extracts.viewReports.available = true;

      // Calculate if general sub sections are available for editing
      actualStatus.swaps.available = actualStatus.swaps.productsToSwap.available;
      actualStatus.extracts.available = true;
    }
    return actualStatus;
  });

  const selectedScenarioStatus = computed(() => {
    return scenarioStatus.value(state.selectedScenario);
  });

  // Get the job statuses - uses a base object to avoid errors.
  const jobStatuses = computed(() => {
    const baseJobObject = {
      jobId: null,
      status: null,
      dateTimeComplete: null,
      progress: null,
    };

    const baseJobStatus = {
      switching: { ...baseJobObject },
      prepareSwaps: { ...baseJobObject },
      canvasGeneration: { ...baseJobObject },
      copyScenario: { ...baseJobObject },
      cdtGeneration: {},
      clusteringGenerator: { ...baseJobObject },
      regionsByAttributeGenerator: { ...baseJobObject },
      clusteredSwitching: { ...baseJobObject },
      spacebreakGenerator: { ...baseJobObject },
      clScSwitchingMatricesCalculation: { ...baseJobObject },
      setupProductModelling: { ...baseJobObject },
    };

    // Map on real status
    return merge(baseJobStatus, state.selectedScenario?.jobs || {});
  });

  // TODO? - Should live in the jobs module? It could then also allow for the assortment style jobs rather than just scenario
  // Can be used now only if selected scenario is set in a state
  const isJobRunning = computed(() => (jobName, subTaskId) => {
    const job = jobStatuses.value[jobName];
    if (!job) return false;

    const subTaskStatus = subTaskId ? job[subTaskId]?.status : job.status;
    if (!subTaskStatus) return false;

    return !includes([jobapi.jobStatuses.finished, jobapi.jobStatuses.failed], subTaskStatus);
  });

  const getJob = computed(() => jobName => {
    return get(jobStatuses.value, jobName);
  });

  /**
   * Actions
   */

  async function fetchScenarioCannGroups() {
    const scenarioId = state.selectedScenario._id;
    const [err, response] = await to(axios.get(`/api/scenarios/${scenarioId}/cann-groups`));
    if (err) throw new Error(err.message);
    return response.data;
  }

  async function fetchScenarios({ params = {}, options = {} } = {}) {
    state.loading = true;
    const defaultSortParams = {
      sortField: 'creationDate',
      sortDirection: SORT_DIRECTION.ascending,
    };
    const [err, response] = await to(
      axios.get('/api/scenarios/', { params: { ...defaultSortParams, ...params } })
    );
    state.loading = false;
    if (err) throw new Error(err.message);
    if (options.skipCommit !== true) state.scenarios = response.data;
    return response.data;
  }

  async function updateScenarioJobAndStatus({ message } = {}) {
    if (!state.selectedScenario) return;

    const scenarioId = get(message, 'scenarioId', state.selectedScenario._id);

    const pick = ['_id', 'status', 'jobs'];

    const [err, res] = await to(axios.get(`/api/scenarios/${scenarioId}`, { params: { pick } }));

    if (err) throw new Error(err.message);

    const { _id, status, jobs } = res.data.data;

    if (status && scenarioId === _id) {
      state.selectedScenario.status = status;
    }

    if (jobs) {
      const scenarioIndex = state.scenarios.findIndex(scenario => scenario._id === scenarioId);

      if (scenarioIndex !== -1) {
        state.scenarios[scenarioIndex].jobs = jobs;
      }

      if (scenarioId === state.selectedScenario._id) {
        state.selectedScenario.jobs = jobs;
      }
    }
  }

  async function loadScenario(id) {
    const eventsStore = useEventsStore();
    state.loading = true;
    const [err, { data: result }] = await to(axios.get(`/api/scenarios/${id}`));
    state.loading = false;
    if (err) throw new Error(err.message);
    const hasChangedScenario = !(id === state.selectedScenario._id);
    state.selectedScenario = result.data;
    updateScenarioJobAndStatus();

    if (hasChangedScenario) {
      // Open event stream for events for this sceanrio
      eventsStore.resetEventStreamWithScenarioId({ scenarioId: id });
    }

    return result.data;
  }

  async function createScenario({ scenario }) {
    state.saveInProgress = true;
    const [err, response] = await to(axios.post('/api/scenarios', scenario));
    state.saveInProgress = false;
    if (err) throw new Error(err.message);
    // TODO: [AOV3-325] Check how server specifies mongo errors we rely on {data.errors}, that are not presented in Mongo Error.
    // Instead we have {name: 'Error', message }
    if (response.data.name === 'Error') {
      throw new Error(response.data.message);
    }
    return response.data;
  }

  async function syncTransferPrice({ scenario }) {
    function sortTransferPrice(transferPriceRange) {
      const { dayjs } = useDayjs();
      const [transferPriceStartDate, transferPriceEndDate] = Object.values(transferPriceRange).sort(
        (a, b) => dayjs.utc(a).diff(dayjs.utc(b))
      );
      return { transferPriceStartDate, transferPriceEndDate };
    }
    scenario.transferPriceRange = sortTransferPrice(scenario.transferPriceRange);

    // note this does not update the scenario in vuex! updateScenariosMutation does
    state.saveInProgress = true;
    const [err, response] = await to(
      axios.post(`/api/scenarios/${state.selectedScenario._id}/sync-transfer-price`, scenario)
    );
    state.saveInProgress = false;
    if (err) throw new Error(err.message);
    return response.data;
  }

  async function updateScenario({ scenario = {} }) {
    // note this does not update the scenario in vuex! updateScenariosMutation does
    state.saveInProgress = true;
    const [err, response] = await to(axios.patch('/api/scenarios', scenario));
    if (err) throw new Error(err.message);
    state.saveInProgress = false;
    return response.data;
  }

  async function deleteScenario(scenario) {
    state.loading = true;
    const [err, response] = await to(axios.delete(`/api/scenarios/${scenario._id}`));
    state.loading = false;
    // Refresh final workpackages, in case this scenario contained the last final canvas.
    useAssortmentCanvasStore().fetchFinalWorkpackages();
    if (err) throw new Error(err.message);
    state.scenarios = state.scenarios.filter(el => el._id !== scenario._id);
    return response;
  }

  function setSelectedScenario(scenario) {
    const eventsStore = useEventsStore();

    state.selectedScenario = scenario;

    if (scenario) {
      // Open event stream for events for this scenario
      eventsStore.resetEventStreamWithScenarioId({ scenarioId: scenario._id });
    }
  }

  async function copyScenario(scenario) {
    state.loading = true;
    setSelectedScenario(scenario);
    const [err, response] = await to(axios.post(`/api/scenarios/copy/${scenario._id}`));
    state.loading = false;
    updateScenarioJobAndStatus();
    if (err) throw new Error(err.message);
    return response;
  }

  async function prepareSwaps() {
    const { _id: scenarioId } = state.selectedScenario;
    state.loading = true;
    const [err, response] = await to(axios.post(`/api/scenarios/${scenarioId}/prepare-swaps`));
    state.loading = false;
    updateScenarioJobAndStatus();
    if (err) throw new Error(err.message);
    return response;
  }

  async function refreshScenario() {
    const { _id } = state.selectedScenario;
    await loadScenario(_id);
  }

  async function afterProductModelingSetupComplete() {
    refreshScenario();
  }

  async function afterCopyScenarioFinishes(message) {
    const wpStore = useWorkpackagesStore();
    const [err, res] = await to(axios.get(`/api/scenarios/${message.scenarioId}`));
    if (err) throw new Error(err.message);
    const scenario = res.data.data;

    const isMessageComingFromCurrentWp =
      message.workpackageId === get(wpStore.selectedWorkpackage, '_id');
    if (isMessageComingFromCurrentWp) {
      await fetchScenarios({ params: { where: { workpackageId: message.workpackageId } } });
    }

    return scenario;
  }

  async function afterCopyScenarioComplete({ message }) {
    const snackbarStore = useSnackbarStore();
    const scenario = await afterCopyScenarioFinishes(message);
    const content = i18n.t('notifications.copyScenario.finished', { name: scenario.name });
    const color = 'success';
    snackbarStore.showSnackbar({ content, color });
  }

  async function afterCopyScenarioFailed({ message }) {
    const snackbarStore = useSnackbarStore();
    const scenario = await afterCopyScenarioFinishes(message);
    const content = i18n.t('notifications.copyScenario.failed', { name: scenario.name });
    const color = 'error';
    snackbarStore.showSnackbar({ content, color });
  }

  async function afterPrepareSwapsComplete({ message }) {
    const snackbarStore = useSnackbarStore();
    const [err, res] = await to(axios.get(`/api/scenarios/${message.scenarioId}`));
    if (err) throw new Error(err.message);

    const scenario = res.data.data;
    const content = i18n.t('notifications.prepareSwaps.finished', { name: scenario.name });
    const color = 'success';
    snackbarStore.showSnackbar({ content, color });
    refreshScenario();
  }

  async function afterPrepareSwapsFailed({ message }) {
    const snackbarStore = useSnackbarStore();
    const [err, res] = await to(axios.get(`/api/scenarios/${message.scenarioId}`));
    if (err) throw new Error(err.message);

    const scenario = res.data.data;
    const content = i18n.t('notifications.prepareSwaps.failed', { name: scenario.name });
    const color = 'error';
    snackbarStore.showSnackbar({ content, color });
    refreshScenario();
  }

  async function updateScenarioCannGroups({
    id,
    cannGroups: newCannGroups,
    commitChanges,
    cannGroupsChanged,
  }) {
    state.saveInProgress = true;
    const [err, result] = await to(
      axios.patch(`/api/scenarios/${id}/update-property/cannGroups`, {
        cannGroups: newCannGroups,
        commit: commitChanges,
        cannGroupsChanged,
      })
    );
    if (err) throw new Error(err.message);
    state.saveInProgress = false;
    updateScenariosMutation(result.data);
    return result;
  }

  async function updateOptimiserSettings({ id, optimiserSettings }) {
    state.saveInProgress = true;
    const [err, { data: result }] = await to(
      axios.patch(`/api/scenarios/${id}/optimiserSettings`, { optimiserSettings })
    );
    if (err) throw new Error(err.message);
    state.saveInProgress = false;
    updateScenariosMutation(result.data);
    return result;
  }

  async function updateVarietyGroups({ id, varietyGroups }) {
    state.saveInProgress = true;
    const [err, { data: result }] = await to(
      axios.patch(`/api/scenarios/${id}/update-property/varietyGroups`, { varietyGroups })
    );
    if (err) throw new Error(err.message);
    state.saveInProgress = false;
    updateScenariosMutation(result.data);
    return result;
  }

  async function hasValidProductAttributes({ scenarioId, optimiseAccordingTo }) {
    state.loading = true;
    const [err, result] = await to(
      axios.get(`/api/scenarios/${scenarioId}/hasValidProductAttributes/${optimiseAccordingTo}`)
    );
    state.loading = false;
    if (err) return false;
    return result.data;
  }

  async function editCustomAttributes({
    scenario,
    addedCustomAttributes,
    deletedCustomAttributes,
  }) {
    // This is used in attribute-editor and takes over a second on the large NFR
    // Should be looked over for performance improvements
    // Also, actions should be asynchronous - this seems odd.
    const oldCustomAttributes = scenario.customAttributes || [];
    const newCustomAttributesRevised = (addedCustomAttributes || []).map(attr => {
      let additionalAttrs = {};
      const attributeKey = get(attr, 'attributeKey', null);
      const attributeSource = get(attr, 'attributeSource', null);
      if (attributeKey && attributeSource) {
        additionalAttrs = {
          attributeKey,
          attributeSource,
        };
      }
      return {
        name: attr.text,
        id: attr.value,
        clean: attr.clean,
        source: attr.source,
        ...additionalAttrs,
      };
    });
    const updatedScenario = {
      ...scenario,
      customAttributes: [...oldCustomAttributes, ...newCustomAttributesRevised].filter(
        ({ id: attribute }) => !deletedCustomAttributes.includes(attribute)
      ),
    };

    setSelectedScenario(updatedScenario);
    updateScenariosMutation(updatedScenario);
    return updatedScenario;
  }

  async function setScenarioHasSkippedClustering({ hasSkippedClustering }) {
    const snackbarStore = useSnackbarStore();
    const scenarioId = state.selectedScenario._id;
    const res = await updateScenario({
      scenario: {
        id: scenarioId,
        hasSkippedClustering,
      },
    });
    await refreshScenario();

    if (get(res, 'data.matchedCount') === 1) {
      snackbarStore.showSuccess(i18n.t('actions.saveSuccess'));
    } else {
      snackbarStore.showError(i18n.t('errors.generalErrorMessage'));
    }
  }

  async function setScenarioHasSkippedRegionsByAttribute({ hasSkippedRegionsByAttribute }) {
    const snackbarStore = useSnackbarStore();
    const scenarioId = state.selectedScenario._id;
    const res = await updateScenario({
      scenario: {
        id: scenarioId,
        'regionSettings.hasSkippedRegionsByAttribute': hasSkippedRegionsByAttribute,
      },
    });
    await refreshScenario();

    if (get(res, 'data.matchedCount') !== 1) {
      snackbarStore.showError(i18n.t('errors.generalErrorMessage'));
    }
  }

  async function runGenerateRegionsByAttribute() {
    const scenarioId = state.selectedScenario._id;
    state.loading = true;
    const url = `/api/scenarios/${scenarioId}/regions-by-attribute/generate`;
    const [err] = await to(axios.post(url));
    state.loading = false;
    await refreshScenario();
    if (err) throw new Error(err.message);
  }

  async function setScenarioOptionalAttributes({
    useMinimumFacings,
    useMinimumDistribution,
    additionalFields,
  }) {
    const snackbarStore = useSnackbarStore();
    const scenarioId = state.selectedScenario._id;
    const res = await updateScenario({
      scenario: {
        id: scenarioId,
        'optionalAttributes.useMinimumFacings': useMinimumFacings,
        'optionalAttributes.useMinimumDistribution': useMinimumDistribution,
        'optionalAttributes.additionalFields': additionalFields,
      },
    });
    await refreshScenario();

    if (get(res, 'data.matchedCount') === 1) {
      snackbarStore.showSuccess(i18n.t('actions.saveSuccess'));
    } else {
      snackbarStore.showError(i18n.t('errors.generalErrorMessage'));
    }
  }

  return {
    // state
    ...toRefs(state),

    // mutations
    updateScenariosMutation,
    setScenarioCannGroupsWithNotesMutation,
    updateCannGroupNoteCountMutation,
    updateNoteCountMutation,

    // getters
    getScenarioById,
    cannGroups,
    selectedScenarioStatus,
    stores,
    storesInAssortmentCount,
    attributesById,
    getCustomerFacingAndReportingAttributes,
    getSimpleSwapsClusteredStores,
    scenarioStatus,
    jobStatuses,
    isJobRunning,
    getJob,
    getUseMarkdownAndWasteToCalculateMarginAndPrice,

    // actions
    fetchScenarioCannGroups,
    fetchScenarios,
    loadScenario,
    createScenario,
    syncTransferPrice,
    updateScenario,
    deleteScenario,
    copyScenario,
    prepareSwaps,
    afterProductModelingSetupComplete,
    afterCopyScenarioFinishes,
    afterCopyScenarioComplete,
    afterCopyScenarioFailed,
    afterPrepareSwapsComplete,
    afterPrepareSwapsFailed,
    updateScenarioCannGroups,
    setScenarioHasSkippedClustering,
    setScenarioHasSkippedRegionsByAttribute,
    runGenerateRegionsByAttribute,
    setScenarioOptionalAttributes,
    editCustomAttributes,
    setSelectedScenario,
    refreshScenario,
    updateOptimiserSettings,
    updateVarietyGroups,
    hasValidProductAttributes,
    updateScenarioJobAndStatus,
  };
});
